import { allSteelSubCategories, categories, type Item } from "./data";
import { steelSpecConfigs } from "./steelSpecConfig";

export const REAL_CATEGORY_IDS = new Set(
  categories
    .map((category) => category.id)
    .filter((id) => id !== "all" && id !== "uncategorized"),
);

export const CATEGORY_NAME_TO_ID = new Map(
  categories.map((category) => [category.name, category.id]),
);

export const CATEGORY_ID_TO_NAME = new Map(
  categories.map((category) => [category.id, category.name]),
);

export function normalizeCategoryId(raw: string | null): string {
  const value = raw ?? "";
  if (REAL_CATEGORY_IDS.has(value)) return value;
  return CATEGORY_NAME_TO_ID.get(value) ?? value;
}

export function normalizeSubType(raw: string | null): string | undefined {
  if (!raw) return undefined;
  return raw.split(" - ")[0].trim();
}

export function toServerMainCategory(categoryId: string | null): string | null {
  if (!categoryId) return null;
  return CATEGORY_ID_TO_NAME.get(categoryId) ?? categoryId;
}

export function toServerSubCategory(
  categoryId: string | null,
  subType: string | null | undefined,
): string | null {
  if (!subType) return null;
  if (categoryId === "steel") {
    const name = allSteelSubCategories.find((s) => s.id === subType)?.name;
    return name ? `${subType} - ${name}` : subType;
  }
  return subType;
}

export type ServerItemRow = {
  id: string;
  mainCategory: string | null;
  subCategory: string | null;
  sectionDimension: unknown;
  name: string;
  description: string | null;
};

const DIMENSION_KEY_ORDER = ["h", "b", "t1", "t2"] as const;

export function parseSpec(
  value: unknown,
  steelType?: string,
): string | undefined {
  if (value == null) return undefined;
  if (typeof value === "string") return value || undefined;
  if (typeof value !== "object") return undefined;
  const obj = value as Record<string, unknown>;
  if (typeof obj.spec === "string") return obj.spec || undefined;
  const dims = DIMENSION_KEY_ORDER.map((key) => obj[key]).filter(
    (v): v is number => typeof v === "number",
  );
  if (dims.length === 0) return undefined;
  const fieldCount = steelType
    ? steelSpecConfigs[steelType]?.fields.length
    : undefined;
  return (fieldCount ? dims.slice(0, fieldCount) : dims).join("X");
}

export function mapServerItem(row: ServerItemRow, isPreset: boolean): Item {
  const categoryId = normalizeCategoryId(row.mainCategory);
  const steelType = normalizeSubType(row.subCategory);
  return {
    id: row.id,
    name: row.name,
    category: categories.find((c) => c.id === categoryId)?.name ?? categoryId,
    categoryId,
    steelType,
    spec: parseSpec(row.sectionDimension, steelType),
    description: row.description ?? undefined,
    isActive: true,
    isPreset,
  };
}
